import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useCreateAdminUserMutation,
  useGetAdminUserQuery,
  useGetAdminUsersQuery,
  useUpdateAdminUserMutation
} from '@bo-services/userApi';
import { DEFAULT_USER_SORT } from '@library/constants/user';
import type { SortUserColumn } from '@library/types/user';
import { delayPromise } from '@bo-utils/common';

export const useAdminUsers = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchValue, setSearchValue] = useState<string>('');
  const [filteredRoles, setFilteredRoles] = useState<string[]>([]);
  const [sort, setSort] = useState<SortUserColumn>(DEFAULT_USER_SORT);
  const searchValueRef = useRef<string>(searchValue);
  const sortValue =
    sort.sortDirection !== 'none' ? `${sort.sortBy}:${sort.sortDirection}` : '';

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deactivateUserModalOpen, setDeactivateUserModalOpen] = useState(false);
  const [editUserDrawerOpen, setEditUserDrawerOpen] = useState(false);
  const [viewPermissionsDrawerOpen, setViewPermissionsDrawerOpen] =
    useState(false);

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isFetchingUpdatedUsers, setIsFetchingUpdatedUsers] = useState(false);

  const {
    data: userData,
    isLoading: isLoadingUserRoles,
    isFetching: isFetchingUserRoles,
    refetch: refetchUsersData
  } = useGetAdminUsersQuery({
    page: currentPage,
    role_ids: filteredRoles,
    search: searchValue,
    sort: sortValue
  });

  const {
    data: selectedUserData,
    isLoading: isLoadingSelectedUser,
    isFetching: isFetchingSelectedUser,
    refetch: refetchSelectedUserData
  } = useGetAdminUserQuery(
    {
      id: selectedUserId
    },
    {
      skip: !selectedUserId
    }
  );

  const [createAdminUser] = useCreateAdminUserMutation();

  const [updateAdminUser] = useUpdateAdminUserMutation();

  useEffect(() => {
    if (searchValue !== searchValueRef.current) {
      setCurrentPage(1);
      searchValueRef.current = searchValue;
    }
  }, [searchValue]);

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleSort = (sortUserColumn: SortUserColumn) => {
    setSort(sortUserColumn);
  };

  const handleUsersUpdate = useCallback(async () => {
    setIsFetchingUpdatedUsers(true);

    const { promise } = delayPromise(250);
    await promise.then(async () => {
      await refetchUsersData();
      await refetchSelectedUserData();
    });

    setIsFetchingUpdatedUsers(false);
    setEditUserDrawerOpen(false);
  }, [refetchSelectedUserData, refetchUsersData]);

  const userRolesContext = useMemo(() => {
    return {
      createAdminUser,
      filters: {
        currentPage,
        filteredRoles,
        onSearch: handleSearch,
        onCurrentPageChange: setCurrentPage,
        onFilteredRolesChange: setFilteredRoles,
        searchValue
      },
      isSelectedUserLoading: isFetchingUpdatedUsers
        ? false
        : isLoadingSelectedUser || isFetchingSelectedUser,
      isUserRolesLoading: isFetchingUpdatedUsers
        ? false
        : isLoadingUserRoles || isFetchingUserRoles,
      modals: {
        createModalOpen,
        deactivateUserModalOpen,
        editUserDrawerOpen,
        onCreateModalOpen: setCreateModalOpen,
        onDeactivateUserModalOpen: setDeactivateUserModalOpen,
        onEditUserDrawerOpen: setEditUserDrawerOpen,
        onViewPermissionsDrawerOpen: setViewPermissionsDrawerOpen,
        viewPermissionsDrawerOpen
      },
      onSelectedUserId: setSelectedUserId,
      onSort: handleSort,
      onUsersUpdate: handleUsersUpdate,
      selectedUserData,
      sort,
      updateAdminUser,
      userData
    };
  }, [
    createAdminUser,
    currentPage,
    handleUsersUpdate,
    isFetchingUpdatedUsers,
    updateAdminUser,
    createModalOpen,
    deactivateUserModalOpen,
    editUserDrawerOpen,
    filteredRoles,
    isLoadingUserRoles,
    isFetchingUserRoles,
    isLoadingSelectedUser,
    isFetchingSelectedUser,
    searchValue,
    selectedUserData,
    sort,
    userData,
    viewPermissionsDrawerOpen
  ]);

  return {
    userRolesContext
  };
};
